import React, { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  Mail, 
  Phone, 
  MessageSquare, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Users,
  Building
} from 'lucide-react';

// Constants
const RECAPTCHA_SITE_KEY = '6LdAiogrAAAAAB0Pf7jllalfvUwoHQQwhSfjrMZk';

// Types
type FormStatus = 'idle' | 'success' | 'error';
type ContactMethod = {
  icon: React.ReactNode;
  title: string;
  description: string;
  value: string;
  link: string;
};

// Data outside component to prevent recreation
const CONTACT_METHODS: ContactMethod[] = [
  {
    icon: <Mail className="w-6 h-6" />,
    title: "Email Us",
    description: "Send us an email anytime",
    value: "support@tooliq.ai",
    link: "mailto:support@tooliq.ai"
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: "Live Chat",
    description: "We're here to help",
    value: "Coming Soon",
    link: "#"
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Community",
    description: "Join our community",
    value: "Discord Server",
    link: "#"
  }
];

const SUBJECT_OPTIONS = [
  { value: "", label: "What can we help you with?" },
  { value: "Feedback", label: "Product Feedback" },
  { value: "Partnership", label: "Business Partnership" },
  { value: "Support", label: "Technical Support" },
  { value: "Report a Tool", label: "Report a Tool" },
  { value: "Other", label: "Other" }
] as const;

const WHY_CONTACT_US = [
  "Get personalized AI tool recommendations",
  "Report issues with existing tools",
  "Suggest new features or improvements",
  "Explore partnership opportunities",
  "Get technical support and guidance"
];

const EXPECTATIONS = [
  {
    number: 1,
    color: "green",
    title: "Quick Response",
    description: "We typically reply within 24 hours"
  },
  {
    number: 2,
    color: "blue",
    title: "Expert Assistance",
    description: "Get help from our AI specialists"
  },
  {
    number: 3,
    color: "purple",
    title: "Follow-up Support",
    description: "We ensure your issue is fully resolved"
  }
];

const Contact: React.FC = () => {
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Memoized SEO data
  const seoData = useMemo(() => ({
    title: "Contact Us | TooliQ.ai",
    description: "Get in touch with TooliQ.ai team for support, partnerships, or feedback.",
    canonical: `https://tooliq.ai${window.location.pathname}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact TooliQ.ai",
      "description": "Reach out to TooliQ.ai for support, suggestions, or business partnerships.",
      "url": "https://tooliq.ai/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "TooliQ.ai",
        "url": "https://tooliq.ai",
        "logo": "https://tooliq.ai/image/tooliq.webp",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "Customer Support",
          "email": "support@tooliq.ai",
          "availableLanguage": ["English"],
          "areaServed": "Worldwide"
        }
      }
    }
  }), []);

  // Event handlers
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!captchaVerified) {
      setFormStatus('error');
      setIsSubmitting(false);
      return;
    }

    const subject = selectedSubject === 'Other' ? customSubject : selectedSubject;

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Form submitted with subject:', subject);
      setFormStatus('success');
    } catch (error) {
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }, [captchaVerified, selectedSubject, customSubject]);

  const handleSubjectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubject(e.target.value);
  }, []);

  const handleCustomSubjectChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomSubject(e.target.value);
  }, []);

  const handleRecaptchaChange = useCallback((token: string | null) => {
    setCaptchaVerified(!!token);
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setCaptchaVerified(false);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.structuredData)}
        </script>
      </Helmet>

      {/* Header Section */}
      <HeaderSection />

      {/* Contact Methods */}
      <ContactMethodsSection />

      {/* Main Content */}
      <MainContentSection
        selectedSubject={selectedSubject}
        customSubject={customSubject}
        formStatus={formStatus}
        isSubmitting={isSubmitting}
        captchaVerified={captchaVerified}
        onSubjectChange={handleSubjectChange}
        onCustomSubjectChange={handleCustomSubjectChange}
        onRecaptchaChange={handleRecaptchaChange}
        onRecaptchaExpired={handleRecaptchaExpired}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

// Extracted HeaderSection component
const HeaderSection: React.FC = () => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-800"></div>
    <div className="relative max-w-7xl mx-auto px-6 py-20 text-center">
      <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-slate-800 via-purple-600 to-blue-600 dark:from-slate-100 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent mb-6">
        Get in Touch
      </h1>
      <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
        We're here to help and answer any questions you might have. 
        We look forward to hearing from you.
      </p>
    </div>
  </div>
);

// Extracted ContactMethodsSection component
const ContactMethodsSection: React.FC = () => (
  <div className="relative max-w-7xl mx-auto px-6">
    <div className="grid md:grid-cols-3 gap-8 mb-16">
      {CONTACT_METHODS.map((method, index) => (
        <a
          key={index}
          href={method.link}
          className="group bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform duration-300 mb-4">
            {method.icon}
          </div>
          <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
            {method.title}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">
            {method.description}
          </p>
          <p className="text-purple-600 dark:text-purple-400 font-medium">
            {method.value}
          </p>
        </a>
      ))}
    </div>
  </div>
);

// Extracted MainContentSection component
interface MainContentSectionProps {
  selectedSubject: string;
  customSubject: string;
  formStatus: FormStatus;
  isSubmitting: boolean;
  captchaVerified: boolean;
  onSubjectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCustomSubjectChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRecaptchaChange: (token: string | null) => void;
  onRecaptchaExpired: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

const MainContentSection: React.FC<MainContentSectionProps> = ({
  selectedSubject,
  customSubject,
  formStatus,
  isSubmitting,
  captchaVerified,
  onSubjectChange,
  onCustomSubjectChange,
  onRecaptchaChange,
  onRecaptchaExpired,
  onSubmit
}) => (
  <div className="relative max-w-7xl mx-auto px-6 pb-20">
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      {/* Contact Form */}
      <ContactForm 
        selectedSubject={selectedSubject}
        customSubject={customSubject}
        formStatus={formStatus}
        isSubmitting={isSubmitting}
        captchaVerified={captchaVerified}
        onSubjectChange={onSubjectChange}
        onCustomSubjectChange={onCustomSubjectChange}
        onRecaptchaChange={onRecaptchaChange}
        onRecaptchaExpired={onRecaptchaExpired}
        onSubmit={onSubmit}
      />

      {/* Info Section */}
      <InfoSection />
    </div>
  </div>
);

// Extracted ContactForm component
interface ContactFormProps extends MainContentSectionProps {}

const ContactForm: React.FC<ContactFormProps> = ({
  selectedSubject,
  customSubject,
  formStatus,
  isSubmitting,
  captchaVerified,
  onSubjectChange,
  onCustomSubjectChange,
  onRecaptchaChange,
  onRecaptchaExpired,
  onSubmit
}) => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-slate-200 dark:border-slate-700">
    <div className="flex items-center gap-3 mb-8">
      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
        <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
        Send us a Message
      </h2>
    </div>

    <form onSubmit={onSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <FormInput
          id="name"
          label="Full Name *"
          type="text"
          required
          placeholder="John Doe"
        />
        
        <FormInput
          id="email"
          label="Email Address *"
          type="email"
          required
          placeholder="john@example.com"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <FormInput
          id="phone"
          label="Phone Number"
          type="tel"
          placeholder="+1 (555) 000-0000"
        />
        
        <FormInputWithIcon
          id="company"
          label="Company"
          icon={<Building className="w-4 h-4 text-slate-400" />}
          placeholder="Your company"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="subject" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Subject *
        </label>
        <select
          id="subject"
          value={selectedSubject}
          onChange={onSubjectChange}
          required
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 appearance-none"
        >
          {SUBJECT_OPTIONS.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {selectedSubject === 'Other' && (
        <FormInput
          id="customSubject"
          label="Please specify *"
          type="text"
          value={customSubject}
          onChange={onCustomSubjectChange}
          required
          placeholder="Tell us how we can help..."
        />
      )}

      <div className="space-y-2">
        <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Message *
        </label>
        <textarea
          id="message"
          rows={5}
          required
          className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 resize-none"
          placeholder="Please describe your inquiry in detail..."
        />
      </div>

      <div className="flex justify-center">
        <ReCAPTCHA
          sitekey={RECAPTCHA_SITE_KEY}
          onChange={onRecaptchaChange}
          onExpired={onRecaptchaExpired}
          className="transform scale-105"
        />
      </div>

      <SubmitButton isSubmitting={isSubmitting} captchaVerified={captchaVerified} />

      {/* Form Status Messages */}
      {formStatus === 'success' && <SuccessMessage />}
      {formStatus === 'error' && <ErrorMessage />}
    </form>
  </div>
);

// Extracted FormInput component
interface FormInputProps {
  id: string;
  label: string;
  type?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
}

const FormInput: React.FC<FormInputProps> = ({
  id,
  label,
  type = 'text',
  value,
  onChange,
  required = false,
  placeholder
}) => (
  <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
      placeholder={placeholder}
    />
  </div>
);

// Extracted FormInputWithIcon component
interface FormInputWithIconProps extends Omit<FormInputProps, 'type' | 'value' | 'onChange'> {
  icon: React.ReactNode;
}

const FormInputWithIcon: React.FC<FormInputWithIconProps> = ({
  id,
  label,
  icon,
  placeholder
}) => (
  <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-medium text-slate-700 dark:text-slate-300">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
        {icon}
      </div>
      <input
        id={id}
        type="text"
        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
        placeholder={placeholder}
      />
    </div>
  </div>
);

// Extracted SubmitButton component
interface SubmitButtonProps {
  isSubmitting: boolean;
  captchaVerified: boolean;
}

const SubmitButton: React.FC<SubmitButtonProps> = ({ isSubmitting, captchaVerified }) => (
  <button
    type="submit"
    disabled={isSubmitting || !captchaVerified}
    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
  >
    {isSubmitting ? (
      <>
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        Sending...
      </>
    ) : (
      <>
        <Send className="w-5 h-5" />
        Send Message
      </>
    )}
  </button>
);

// Extracted SuccessMessage component
const SuccessMessage: React.FC = () => (
  <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
    <div>
      <p className="text-green-800 dark:text-green-300 font-medium">
        Message sent successfully!
      </p>
      <p className="text-green-700 dark:text-green-400 text-sm">
        We'll get back to you within 24 hours.
      </p>
    </div>
  </div>
);

// Extracted ErrorMessage component
const ErrorMessage: React.FC = () => (
  <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3 animate-in slide-in-from-bottom duration-300">
    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
    <div>
      <p className="text-red-800 dark:text-red-300 font-medium">
        Please complete the reCAPTCHA
      </p>
      <p className="text-red-700 dark:text-red-400 text-sm">
        This helps us prevent spam messages.
      </p>
    </div>
  </div>
);

// Extracted InfoSection component
const InfoSection: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl p-8 text-white shadow-2xl">
      <h3 className="text-2xl font-bold mb-4">Why Contact Us?</h3>
      <ul className="space-y-4">
        {WHY_CONTACT_US.map((item, index) => (
          <li key={index} className="flex items-center gap-3">
            <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700">
      <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">
        What to Expect
      </h3>
      <div className="space-y-4 text-slate-600 dark:text-slate-400">
        {EXPECTATIONS.map((exp, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className={`w-6 h-6 ${getColorClasses(exp.color)} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
              <span className={`${getTextColor(exp.color)} text-sm font-bold`}>
                {exp.number}
              </span>
            </div>
            <div>
              <p className="font-medium text-slate-700 dark:text-slate-300">{exp.title}</p>
              <p className="text-sm">{exp.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Helper functions for color classes
const getColorClasses = (color: string): string => {
  const colors = {
    green: 'bg-green-100 dark:bg-green-900/30',
    blue: 'bg-blue-100 dark:bg-blue-900/30',
    purple: 'bg-purple-100 dark:bg-purple-900/30'
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

const getTextColor = (color: string): string => {
  const colors = {
    green: 'text-green-600 dark:text-green-400',
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400'
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default React.memo(Contact);